import React from "react";
import {
  Bot,
  ChevronDown,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Search,
  TerminalSquare,
  Wrench,
} from "lucide-react";
import { formatDuration } from "../lib/utils";
import { Badge, ScorePill, SectionHeader } from "./Primitives";
import { useLocale } from "../lib/i18n";

const stringify = (value) => {
  if (typeof value === "string") return value;
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
};

const StepIcon = ({ source, tool }) => {
  if (source === "user") return <CircleUserRound size={15} />;
  if (tool) return <Wrench size={15} />;
  return <Bot size={15} />;
};

const TrajectoryStep = ({ step, query }) => {
  const { t } = useLocale();
  const sourceLabel = step.source === "user"
    ? t("trajectory.sourceUser")
    : step.source === "agent"
      ? t("trajectory.sourceAgent")
      : t("trajectory.unknown");
  const [open, setOpen] = React.useState(false);
  const toolCalls = Array.isArray(step.tool_calls) ? step.tool_calls : [];
  const observations = Array.isArray(step.observation?.results) ? step.observation.results : [];
  const haystack = `${step.message || ""} ${stringify(toolCalls)} ${stringify(observations)}`.toLowerCase();
  if (query && !haystack.includes(query.toLowerCase())) return null;
  const hasDetail = toolCalls.length || observations.length || String(step.message || "").length > 500;
  return (
    <article className={`trajectory-step step-${step.source || "unknown"}`}>
      <button className="step-head" onClick={() => hasDetail && setOpen((value) => !value)}>
        <span className="step-number">{step.step_id ?? "·"}</span>
        <span className="step-source"><StepIcon source={step.source} tool={toolCalls.length} />{sourceLabel}</span>
        {step.model_name ? <Badge>{step.model_name}</Badge> : null}
        {toolCalls.length ? <Badge tone="blue">{toolCalls[0].function_name || t("trajectory.tool")}</Badge> : null}
        <span className="step-preview">{step.message || toolCalls[0]?.function_name || t("trajectory.observation")}</span>
        {hasDetail ? (open ? <ChevronDown size={15} /> : <ChevronRight size={15} />) : null}
      </button>
      {open ? (
        <div className="step-body">
          {step.message ? <pre>{step.message}</pre> : null}
          {toolCalls.map((call, index) => (
            <div className="tool-block" key={call.tool_call_id || index}>
              <div><TerminalSquare size={14} /><strong>{call.function_name}</strong></div>
              <pre>{stringify(call.arguments)}</pre>
            </div>
          ))}
          {observations.map((observation, index) => (
            <div className="observation-block" key={observation.source_call_id || index}>
              <strong>{t("trajectory.observation")}</strong>
              <pre>{stringify(observation.content)}</pre>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
};

const GateStrip = ({ run }) => {
  const { t } = useLocale();
  return (
  <div className="gate-strip">
    {run.gateDetails.map((item, index) => (
      <div className={Number(item.value) === 1 ? "gate-pass" : "gate-fail"} key={`${item.id}-${index}`}>
        <code>{item.id}</code>
        <span>{item.description}</span>
        <strong>{Number(item.value) === 1 ? t("trajectory.pass") : t("trajectory.veto")}</strong>
      </div>
    ))}
  </div>
  );
};

const GradedOutcomeTable = ({ run }) => {
  const { t } = useLocale();
  const [onlyMissed, setOnlyMissed] = React.useState(false);
  const details = run.gradedDetails.filter((item) => !onlyMissed || Number(item.value) < 1);
  if (!run.gradedDetails.length) return null;
  return (
    <section>
      <SectionHeader
        eyebrow={t("trajectory.judgeEyebrow")}
        title={t("trajectory.judgeTitle")}
        aside={
          <label className="toggle-label">
            <input type="checkbox" checked={onlyMissed} onChange={(event) => setOnlyMissed(event.target.checked)} />
            {t("trajectory.onlyMissed")}
          </label>
        }
      />
      <div className="outcome-table panel">
        {details.map((item, index) => (
          <article className={`outcome-row ${Number(item.value) < 1 ? "outcome-missed" : ""}`} key={`${item.id}-${index}`}>
            <code>{item.id || item.name || "—"}</code>
            <span className="outcome-value">{Number(item.value) >= 1 ? t("trajectory.pass") : t("trajectory.miss")}</span>
            <div>
              <p>{item.description || "—"}</p>
              {item.reasoning ? <small>{item.reasoning}</small> : null}
            </div>
            {item.weight !== undefined ? <b>w {item.weight}</b> : null}
          </article>
        ))}
      </div>
    </section>
  );
};

export default function TrajectoryView({ sample, initialRunId, onRunChange }) {
  const { t } = useLocale();
  const [selected, setSelected] = React.useState(initialRunId || sample.runs[0]?.id || "");
  const [query, setQuery] = React.useState("");
  React.useEffect(() => {
    const next = initialRunId && sample.runs.some((run) => run.id === initialRunId)
      ? initialRunId
      : sample.runs[0]?.id || "";
    setSelected(next);
  }, [sample.id, initialRunId]);
  const run = sample.runs.find((item) => item.id === selected) || sample.runs[0];
  if (!run) return <div className="missing-panel panel">{t("trajectory.missing")}</div>;
  const change = (value) => {
    setSelected(value);
    onRunChange?.(value);
  };
  const runStatus = (item) => item.agentStatus === "completed"
    ? t("common.statusCompleted")
    : item.agentStatus === "failed"
      ? t("common.statusFailed")
      : t("common.statusUnknown");
  const toolSteps = run.trajectorySteps.filter((step) => Array.isArray(step.tool_calls) && step.tool_calls.length).length;
  const llmCalls = run.trajectorySteps.reduce((sum, step) => sum + (Number(step.llm_call_count) || 0), 0);
  return (
    <div className="page-stack">
      <section>
        <SectionHeader
          eyebrow={t("trajectory.eyebrow")}
          title={t("trajectory.title")}
          aside={
            <select className="run-select" value={run.id} onChange={(event) => change(event.target.value)}>
              {sample.runs.map((item) => <option value={item.id} key={item.id}>{item.modelLabel} · {t("common.agent", { status: runStatus(item) })}</option>)}
            </select>
          }
        />
        <div className="trajectory-summary panel">
          <div className="model-icon large">{run.modelLabel.startsWith("GPT") ? "G" : "O"}</div>
          <div className="trajectory-title"><strong>{run.modelLabel}</strong><span>{run.model} · {run.solver}</span></div>
          <div className="trajectory-stat"><Clock3 size={14} /><b>{formatDuration(run.durationSec)}</b><span>{t("common.duration")}</span></div>
          <div className="trajectory-stat"><Wrench size={14} /><b>{toolSteps}</b><span>{t("common.toolSteps")}</span></div>
          <div className="trajectory-stat"><Bot size={14} /><b>{llmCalls}</b><span>{t("common.llmCalls")}</span></div>
          <ScorePill value={run.effectiveScore} label={t("common.effective")} compact />
        </div>
      </section>

      {run.gateDetails.length ? (
        <section>
          <SectionHeader eyebrow={t("trajectory.vetoEyebrow")} title={t("trajectory.vetoTitle")} />
          <GateStrip run={run} />
        </section>
      ) : null}

      <section>
        <div className="trajectory-toolbar">
          <div className="search-box trajectory-search"><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("trajectory.search")} /></div>
          <span>{t("common.steps", { count: run.trajectorySteps.length })}</span>
        </div>
        <div className="trajectory-list">
          {run.trajectorySteps.map((step, index) => <TrajectoryStep step={step} query={query} key={step.step_id || index} />)}
        </div>
      </section>
      <GradedOutcomeTable run={run} />
    </div>
  );
}
